import * as React from 'react';
import classnames from 'classnames';
import { isNil } from 'lodash';

import Input from 'components/inputs/input/Input';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Spinner from 'components/spinner/Spinner';

import './action-button-dropdown-control.scss';

import type {
  ActionButtonDropdownColor,
  ActionButtonDropdownCustomSize,
  ActionButtonDropdownHeight,
  ActionButtonDropdownItem,
  ActionButtonDropdownStyle,
  ActionButtonDropdownWidth,
  TextEntry
} from 'types';

type Props = {
  selectedItem: ActionButtonDropdownItem | undefined | null,
  withSelectable?: boolean,
  withSpinner?: boolean,
  label?: string | React.ReactNode,
  color?: ActionButtonDropdownColor,
  width?: ActionButtonDropdownWidth,
  height?: ActionButtonDropdownHeight,
  style?: ActionButtonDropdownStyle,
  isMenuOpen: boolean,
  disabled?: boolean,
  blocked?: boolean,
  hideChevron?: boolean,
  customSize?: ActionButtonDropdownCustomSize,
  CustomButton?: React.ReactNode,
  onInputRefCallback: (a: TextEntry) => void,
  onInputFocus: () => void,
  onInputBlur: () => void,
  onMenuToggle: (a: boolean) => void
};

class ActionButtonDropdownControl extends React.PureComponent<Props> {
  getIconSize(): number {
    const { style, width, height } = this.props;

    if (style === 'thin') {
      return 10;
    }

    if (width === 'minimum' || height === 'minimum') {
      return 13;
    }

    return 15;
  }

  getButtonClasses(): string {
    const { color, width, height, style, disabled, blocked, customSize, isMenuOpen, withSpinner } = this.props;

    return classnames('action-button-dropdown-control', {
      [`action-button-dropdown-control--color-${String(color)}`]: color,
      [`action-button-dropdown-control--width-${String(width)}`]: isNil(customSize?.width) && width,
      [`action-button-dropdown-control--height-${String(height)}`]: isNil(customSize?.height) && height,
      [`action-button-dropdown-control--style-${String(style)}`]: style,
      'action-button-dropdown-control--hovering': isMenuOpen,
      'action-button-dropdown-control--disabled': disabled || withSpinner,
      'action-button-dropdown-control--blocked': blocked
    });
  }

  getButtonProps(): React.HTMLAttributes<HTMLDivElement> {
    const { customSize } = this.props;

    return {
      style: {
        width: customSize?.width || undefined,
        height: customSize?.height || undefined
      }
    };
  }

  handleControlClick = (): void => {
    const { isMenuOpen, onMenuToggle, onInputFocus } = this.props;

    onMenuToggle(!isMenuOpen);
    onInputFocus();
  };

  renderSpinner(): React.ReactNode {
    return (
      <div className="action-button-dropdown-control__spinner">
        <Spinner
          position="inline"
          size="tiny"
          color="light"
        />
      </div>
    );
  }

  renderIcon(): React.ReactNode {
    const { color, style } = this.props;
    const iconSize = this.getIconSize();

    const iconClasses = classnames('action-button-dropdown-control__icon', {
      [`action-button-dropdown-control__icon--style-${String(style)}`]: style
    });

    return (
      <div className={iconClasses}>
        <SVGDynamicIcon
          name="chevron-down-custom-fg"
          width={iconSize}
          height={iconSize}
          customColor={color === 'primary' ? 'white' : null}
        />
      </div>
    );
  }

  // though hidden behind control, <input> is required for
  // closing menu when clicking outside of menu area
  renderInput(): React.ReactNode {
    const { onInputBlur, onInputRefCallback } = this.props;

    return (
      <Input
        // TODO: Change Input.js `ref` implementation to the newer `createRef` style
        // $FlowIgnore - Flow doesn't like that `ref` in Input.js can be `null`
        refCallback={onInputRefCallback}
        type="text"
        name="action-button-dropdown-control-input"
        value=""
        autoComplete="off"
        className="action-button-dropdown-control__input"
        onBlur={onInputBlur}
        onChange={() => {}}
      />
    );
  }

  renderLabel(): string | React.ReactNode | undefined | null {
    const { selectedItem, label, withSelectable } = this.props;

    if (isNil(label)) {
      return null;
    }

    if (withSelectable && !isNil(selectedItem)) {
      return selectedItem.label;
    }

    return label;
  }

  renderCustomControl(): React.ReactNode {
    const { CustomButton, disabled, blocked } = this.props;

    const customClasses = classnames('action-button-dropdown-control__custom', {
      'action-button-dropdown-control__custom--disabled': disabled,
      'action-button-dropdown-control__custom--blocked': blocked
    });

    return (
      <div className={customClasses}>
        {this.renderInput()}
        {CustomButton}
      </div>
    );
  }

  renderControl(): React.ReactNode {
    const { withSpinner, hideChevron } = this.props;

    return (
      <div className={this.getButtonClasses()}>
        <div
          className="action-button-dropdown-control__control"
          {...this.getButtonProps()}
        >
          <div className="action-button-dropdown-control__control-contents">
            {this.renderInput()}
            {this.renderLabel()}
          </div>
        </div>
        {
          !hideChevron &&
            this.renderIcon()
        }
        {
          withSpinner &&
            this.renderSpinner()
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const { CustomButton, disabled, blocked, withSpinner } = this.props;
    const isDisabled = disabled || withSpinner;

    return (
      <div onClick={!isDisabled && !blocked ? this.handleControlClick : undefined}>
        {
          !isNil(CustomButton)
            ? this.renderCustomControl()
            : this.renderControl()
        }
      </div>
    );
  }
}

export default ActionButtonDropdownControl;
